// PLAN-domain-classifier-iter2 (2026-05-11): Detail Panel の「統合元 Entry / 派生元 Article」表示用
// PLAN-cockpit-decision-visibility (2026-05-14): 行操作を再設計
//   - id チップ click = full id をコピー
//   - title click = Cockpit で開く (flex-1、リンク範囲をタイトル全体に)
//   - 🔗 Link button = permalink をコピー
// PLAN-permalink-cockpit-convergence (2026-05-14): permalink view 用に prop 経路追加
//   - resolvedRefs: 解決済み参照を渡されたら自前 fetch をスキップ (未認証 permalink 用)
//   - onOpen: title click 遷移を呼び出し側が指定 (Cockpit=setSelectedItem / permalink=/v/{id} 遷移)
import { useEffect, useState } from 'react'
import { apiFetch } from '../../../api/client'
import { useSelectedItem } from '../../hooks/useSelectedItem'
import { useToast } from './Toast'
import { permalinkHref, itemLinkClick } from '../../lib/itemLink'

interface Props {
  ids: string[]
  itemType: 'entry' | 'article'
  label: string  // 見出し (例: "統合元 Entry (3)")
  /** 解決済み参照 (id → {title, origin})。渡されたら自前 fetch をスキップ */
  resolvedRefs?: Record<string, { title: string; origin?: string }>
  /** title click 時の遷移。未指定なら setSelectedItem */
  onOpen?: (id: string, itemType: 'entry' | 'article') => void
}

interface ItemSummary {
  id: string
  title: string
  origin?: string
}

export function RefList({ ids, itemType, label, resolvedRefs, onOpen }: Props) {
  const [items, setItems] = useState<Record<string, ItemSummary>>({})
  const { setSelectedItem } = useSelectedItem()
  const { show } = useToast()

  useEffect(() => {
    if (ids.length === 0 || resolvedRefs) return
    let cancelled = false
    Promise.all(
      ids.map(async id => {
        try {
          const path = itemType === 'entry' ? `/api/entries/${id}` : `/api/articles/${id}`
          const j = await apiFetch<{ title?: string; origin?: string; article?: { title?: string; origin?: string } }>(path)
          const a = j.article ?? j
          return { id, title: a.title ?? id, origin: a.origin }
        } catch {
          return { id, title: `(取得失敗: ${id.slice(0, 8)})` }
        }
      })
    ).then(results => {
      if (cancelled) return
      const map: Record<string, ItemSummary> = {}
      for (const r of results) map[r.id] = r
      setItems(map)
    })
    return () => { cancelled = true }
  }, [ids, itemType, resolvedRefs])

  if (ids.length === 0) return null

  const open = (id: string) => {
    if (onOpen) onOpen(id, itemType)
    else setSelectedItem({ id, itemType })
  }
  const lookup = (id: string): ItemSummary | undefined =>
    resolvedRefs?.[id] ? { id, ...resolvedRefs[id] } : items[id]

  async function copyId(id: string) {
    try {
      await navigator.clipboard.writeText(id)
      show('ID copied', { kind: 'success' })
    } catch {
      show('コピーに失敗しました', { kind: 'error' })
    }
  }

  async function copyLink(id: string) {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/v/${id}`)
      show('Link copied', { kind: 'success' })
    } catch {
      show('コピーに失敗しました', { kind: 'error' })
    }
  }

  return (
    <section className="text-sm space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-text-subtle">{label}</h3>
      <ul className="space-y-1">
        {ids.map(id => {
          const item = lookup(id)
          const title = item?.title ?? '読み込み中…'
          return (
            <li key={id} className="flex items-center gap-2 group">
              <button
                type="button"
                onClick={() => copyId(id)}
                className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-border-subtle text-text-subtle hover:text-text-base hover:border-border-strong transition flex-shrink-0"
                title={`${id} (click to copy ID)`}
              >
                {id.slice(0, 8)}
              </button>
              <a
                href={permalinkHref(id)}
                onClick={(e) => itemLinkClick(e, () => open(id))}
                className="flex-1 text-left text-text-base hover:text-accent-primary truncate"
                title="開く (Ctrl+クリックで新しいタブ)"
              >
                {title}
              </a>
              {item?.origin && (
                <span className="text-[10px] text-text-subtle flex-shrink-0">{item.origin}</span>
              )}
              <button
                type="button"
                onClick={() => copyLink(id)}
                className="text-[10px] px-1.5 py-0.5 rounded border border-border-subtle text-text-subtle hover:text-text-base hover:border-border-strong transition flex-shrink-0 inline-flex items-center gap-1"
                title="permalink をコピー"
              >
                <span aria-hidden>🔗</span> Link
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
